import React from "react";
import t from "../../../../../../i18n";
import { colors } from "@ui/Theme";
import formatDate from "@ui/i18n/formatDate";
import type { Options } from "@ui/Highcharts";
import { useQOI } from "../../../useQOI";
import { formatPeriodAxis, useReportingPeriodAxis } from "../../../useReportingPeriodAxis";
import { useReportingTooltipRender } from "../../../useReportingTooltipRender";
import { EXPECTED_SERIE_ID, MEASURED_SERIE_ID } from "./useSeries";
import validateInterface from "@ui/lib/validateInterface";
import { ReportingPointMetadata } from "../../../../../Project/Chart/PointMetadata";
import { useTranslations } from "../../../../../../api/useTranslations";
import { Duration } from "luxon";
import type { PerformanceReporting, ReportingPeriod } from "../../../../../../api/useReporting";

interface OptionParamerters {
  reporting: PerformanceReporting;
  reportingPeriod: ReportingPeriod | null;
  series: Highcharts.SeriesOptionsType[];
}

export function useOptions(
  chart: React.RefObject<Highcharts.Chart>,
  { reporting, reportingPeriod, series }: OptionParamerters,
) {
  const translations = useTranslations();
  const [qoi] = useQOI(reporting);

  const initialXAxis = useReportingPeriodAxis(chart, reporting, reportingPeriod);

  const renderTooltip = useReportingTooltipRender();

  const constantTooltipFormatter = React.useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      const points = this.points ?? [];

      const serieValues = points.map(p => ({
        name: p.series.name,
        color: p.color as string,
        value: p.y ?? 0,
        unit: qoi?.unit ?? "",
      }));

      const expectedPoint = points.find(p => p.series.userOptions.id === MEASURED_SERIE_ID);
      const measuredPoint = points.find(p => p.series.userOptions.id === EXPECTED_SERIE_ID);
      const { filledCount, projectCount } = validateInterface(
        ReportingPointMetadata,
        expectedPoint?.point.custom,
      );

      const internalLosses = [
        {
          name: t("reporting.internal_gaps", translations),
          value:
            measuredPoint?.y && expectedPoint?.y ? -1 * (measuredPoint.y - expectedPoint.y) : null,
          unit: qoi?.unit ?? "",
        },
        {
          name: t("reporting.internal_gaps.on_expected", translations),
          value:
            measuredPoint?.y && expectedPoint?.y
              ? ((measuredPoint.y - expectedPoint.y) / expectedPoint.y) * -100
              : null,
          unit: "%",
          maximumFractionDigits: 2,
        },
      ];
      const projectPercent =
        (projectCount / (reporting ? Object.keys(reporting.projectById).length : 1)) * 100;
      return renderTooltip({
        title: formatDate(this.x, "month-date", null),
        subtitle: t(
          "reporting.fleet_performance.tooltip.project_subtitle",
          projectCount,
          filledCount ?? 0,
          Number(projectPercent.toFixed(1)),
        ),
        values: [serieValues, internalLosses],
      });
    },
    [renderTooltip, reporting, qoi?.unit, translations],
  );

  return React.useMemo(
    (): Options => ({
      initial: {
        xAxis: initialXAxis,
      },
      chart: {
        ignoreHiddenSeries: true,
        zoomType: "xy",
      },
      plotOptions: {
        series: {
          // Need to define better matching rules between back & front on dateTime (using luxon ?)
          // + fillGap month + 2 days
          gapSize: Duration.fromObject({ months: 1 }).plus({ days: 2 }).toMillis(),
          gapUnit: "value",
        },
      },
      series,
      tooltip: {
        borderColor: colors.raw.titanium500,
        shared: true,
        formatter: constantTooltipFormatter,
      },
      xAxis: {
        crosshair: true,
      },
      yAxis: {
        id: "performance-measured-expected",
        title: {
          text: qoi?.name,
        },
        labels: {
          unit: qoi?.unit ?? "",
          sign: false,
          useHTML: true,
        },
      },
      navigator: {
        enabled: true,
        xAxis: {
          labels: {
            formatter: formatPeriodAxis,
          },
        },
      },
    }),
    [constantTooltipFormatter, initialXAxis, qoi, series],
  );
}
